import { ArrowLeftOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Card, Empty, Skeleton, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Link, useNavigate } from "react-router-dom";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { useMyCrossAccessOrganizations } from "../hooks/useOrganizations";
import type { Organization } from "../types";

const EMPTY_ROWS: Organization[] = [];

const { Title } = Typography;

function renderStatus(record: Organization) {
	if (record.isLock) {
		return <Tag color="red">Bloklangan</Tag>;
	}
	if (record.isEducational) {
		return <Tag color="green">Ta'lim</Tag>;
	}
	return <Tag>Faol</Tag>;
}

export function OtherOrganizationsPage() {
	const navigate = useNavigate();
	const { page, limit, setPage } = useUrlTableState();

	const { data: organizations, isLoading } = useMyCrossAccessOrganizations({
		page,
		limit,
	});

	const columns: ColumnsType<Organization> = [
		{
			title: "№",
			key: "index",
			width: 60,
			align: "center",
			render: (_: unknown, __: unknown, index: number) => (page - 1) * limit + index + 1,
		},
		{
			title: "Nomi",
			dataIndex: "name",
			key: "name",
			ellipsis: true,
			render: (name: string, record: Organization) => (
				<Link
					to={`/organizations/${record.id}`}
					className="font-medium text-blue-600 hover:underline"
					onClick={(e) => e.stopPropagation()}
				>
					{name}
				</Link>
			),
		},
		{
			title: "Turi",
			key: "type",
			width: 180,
			render: (_: unknown, record: Organization) =>
				record.type ? (
					<Tag color="blue">{record.type.name}</Tag>
				) : (
					<Typography.Text type="secondary">—</Typography.Text>
				),
		},
		{
			title: "Telefon",
			dataIndex: "phoneNumber",
			key: "phoneNumber",
			width: 120,
		},
		{
			title: "Holat",
			key: "status",
			width: 140,
			align: "center",
			render: (_: unknown, record: Organization) => renderStatus(record),
		},
	];

	const tableRows = organizations?.data?.items ?? EMPTY_ROWS;

	return (
		<div>
			{/* Header - same style as org detail page */}
			<div className="mb-6">
				<div className="flex items-center gap-2 mb-3">
					<Button
						type="text"
						icon={<ArrowLeftOutlined />}
						onClick={() => navigate(-1)}
						size="small"
						className="text-gray-500 hover:text-gray-700 -ml-1"
					/>
					<Breadcrumb
						items={[
							{ title: <Link to="/">Asosiy</Link> },
							{ title: <Link to="/organizations">Tashkilotlar</Link> },
							{ title: <span className="text-gray-600">Boshqa tashkilotlar</span> },
						]}
						className="text-sm"
					/>
				</div>
				<Title level={4} className="mb-1! font-semibold! text-xl!">
					Boshqa tashkilotlar
				</Title>
			</div>

			{/* Table in card - same styling as org detail tabs card */}
			<Card className="rounded-lg border-gray-100 shadow-sm" styles={{ body: { padding: 0 } }}>
				<div className="pt-6 px-6 pb-6">
					{isLoading ? (
						<Skeleton active paragraph={{ rows: 5 }} />
					) : (
						<Table
							columns={columns}
							dataSource={tableRows}
							rowKey="id"
							loading={false}
							pagination={{
								current: page,
								pageSize: limit,
								total: organizations?.data?.meta?.total ?? 0,
								onChange: setPage,
								showSizeChanger: false,
								showTotal: (total) => `Jami: ${total} ta tashkilot`,
							}}
							size="middle"
							locale={{
								emptyText: (
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description="Boshqa tashkilotlar topilmadi"
									/>
								),
							}}
							onRow={(org) => ({
								onClick: () => navigate(`/organizations/${org.id}`),
								className: "cursor-pointer",
							})}
						/>
					)}
				</div>
			</Card>
		</div>
	);
}
